import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

// Import food images
import food1 from '../assets/food/image00005.jpeg';
import food2 from '../assets/food/image00003.jpeg';
import food3 from '../assets/food/image00009.jpeg';
import food4 from '../assets/food/image00012.jpeg';

// Import interior images
import interior1 from '../assets/interior/image00018.jpeg';
import interior2 from '../assets/interior/image00019.jpeg';
import interior3 from '../assets/interior/image00034.jpeg';

const HeroContainer = styled.section`
  width: 100%;
  height: 100vh;
  min-height: 700px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 20px;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, 
      rgba(61, 54, 53, 0.4) 0%, 
      rgba(61, 54, 53, 0.2) 30%, 
      rgba(59, 14, 45, 0.3) 80%, 
      rgba(43, 10, 33, 0.6) 100%);
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    height: 80vh;
    min-height: 500px;
    padding: 0 15px;
  }
  
  @media (max-width: 480px) {
    height: 70vh;
    min-height: 450px;
  }
`;

const HeroSlide = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => (props.isActive ? 1 : 0)};
  transition: opacity 1.2s cubic-bezier(0.45, 0, 0.2, 1);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: saturate(1.1) contrast(1.05);
  z-index: -1;
  transform: scale(${props => (props.isActive ? 1.05 : 1)});
  transition: opacity 1.2s ease-in-out, transform 8s ease-out;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 900px;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 15px;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${theme.fonts.heading};
  font-size: 4.5rem;
  font-weight: 700;
  color: ${theme.colors.light};
  margin-bottom: 30px;
  text-shadow: 2px 2px 15px rgba(0, 0, 0, 0.5);
  letter-spacing: 1px;
  position: relative;
  z-index: 1;
  
  &::after {
    content: '';
    display: block;
    width: 100px;
    height: 3px;
    background-color: ${theme.colors.caramel};
    margin: 20px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 3.2rem;
    margin-bottom: 25px;
    
    &::after {
      width: 80px;
      margin: 15px auto 0;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
    margin-bottom: 20px;
    
    &::after {
      width: 60px;
      height: 2px;
      margin: 12px auto 0;
    }
  }
`;

const HeroTagline = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 1.5rem;
  font-weight: 400;
  font-style: italic;
  color: ${theme.colors.light};
  margin-bottom: 60px;
  text-shadow: 1px 1px 8px rgba(0, 0, 0, 0.6);
  max-width: 800px;
  position: relative;
  z-index: 1;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 40px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 35px;
    line-height: 1.4;
  }
`;

const ReservationButton = styled.a`
  font-family: ${theme.fonts.accent};
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: ${theme.colors.light};
  background-color: ${theme.colors.burgundy};
  padding: 16px 32px;
  border: 2px solid ${theme.colors.burgundy};
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.4s ease;
  text-decoration: none;
  box-shadow: ${theme.shadows.medium};
  position: relative;
  z-index: 1;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0%;
    height: 100%;
    background-color: ${theme.colors.light};
    transition: all 0.4s ease;
    z-index: -1;
  }
  
  &:hover {
    color: ${theme.colors.burgundy};
    border-color: ${theme.colors.light};
    
    &::before {
      width: 100%;
    }
  }
  
  &:active {
    transform: translateY(2px);
    box-shadow: ${theme.shadows.small};
  }
  
  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 0.9rem;
    letter-spacing: 2px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 0.85rem;
    letter-spacing: 1.5px;
  }
`;

const HeroIndicators = styled.div`
  position: absolute;
  bottom: 40px;
  display: flex;
  gap: 15px;
  z-index: 1;
  
  @media (max-width: 768px) {
    bottom: 30px;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    bottom: 20px;
    gap: 10px;
  }
`;

const Indicator = styled.button<{ isActive: boolean }>`
  width: ${props => props.isActive ? '35px' : '8px'};
  height: 8px;
  border-radius: 4px;
  background-color: ${props => props.isActive ? theme.colors.caramel : 'rgba(245, 241, 237, 0.5)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isActive ? theme.colors.caramel : 'rgba(245, 241, 237, 0.8)'};
  }
  
  @media (max-width: 480px) {
    width: ${props => props.isActive ? '25px' : '6px'};
    height: 6px;
  }
`;

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { image: food1, alt: "Delicious Pizza" },
    { image: interior1, alt: "Fiori Restaurant Interior" },
    { image: food2, alt: "Italian Pasta" },
    { image: interior2, alt: "Restaurant Ambiance" },
    { image: food3, alt: "Fresh Salad" },
    { image: interior3, alt: "Cozy Dining Experience" },
    { image: food4, alt: "Traditional Italian Dish" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const handleIndicatorClick = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <HeroContainer id="home">
      {slides.map((slide, index) => (
        <HeroSlide
          key={index}
          isActive={index === currentSlide}
          style={{ backgroundImage: `url(${slide.image})` }}
          aria-hidden={index !== currentSlide}
        />
      ))}
      <HeroContent>
        <HeroTitle>Fiori Italian Bistro</HeroTitle>
        <HeroTagline>
          A modern culinary experience honoring tradition with contemporary elegance
        </HeroTagline>
        <ReservationButton
          href="https://www.yelp.com/biz/fiori-italian-pizzeria-folsom"
          target="_blank"
          rel="noopener noreferrer"
        >
          Reserve Your Table
        </ReservationButton>
      </HeroContent>
      <HeroIndicators>
        {slides.map((_, index) => (
          <Indicator
            key={index}
            isActive={index === currentSlide}
            onClick={() => handleIndicatorClick(index)}
            aria-label={`View slide ${index + 1}`}
          />
        ))}
      </HeroIndicators>
    </HeroContainer>
  );
};

export default Hero; 